import axios, { AxiosResponse } from 'axios';
import { currentBrand, headlessCmsVersion } from '../config';
import { CpContentIntegrationApi } from 'cp-xhr';
import { BackendModule, ReadCallback, Services } from 'i18next';
import { CmsContentResolver } from './cmsContentResolver';
import { CmsContentResponse } from './types';

type Options = {
    // Namespaces that are intended to be loaded and  used from the CMS
    cmsNamespaces?: string[];
};

class CmsBackend implements BackendModule {
    type: 'backend' = 'backend' as const;

    private services?: Services;
    private cmsContent: Map<string, CmsContentResponse> = new Map();
    private cmsContentPending: Map<string, Promise<AxiosResponse<CmsContentResponse>>> = new Map();

    private cmsNamespaces: string[] = [];

    constructor(services: Services, options: Options = {}) {
        this.init(services, options);
    }

    init(services: Services, options: Options = {}) {
        this.services = services;
        this.cmsNamespaces = options.cmsNamespaces ?? [];
    }

    read(language: string, namespace: string, callback: ReadCallback): void {
        if (!this.cmsNamespaces.includes(namespace)) {
            // Load translations from /public/locales/{{lng}}/{{ns}}.json
            axios
                .get(`/locales/${language}/${namespace}.json`)
                .then(res => callback(null, res.data))
                .catch(err => callback(err, null));
            return;
        }

        const cmsContentFromCahce = this.cmsContent.get(language);
        if (cmsContentFromCahce) {
            this.handleCmsCallback(cmsContentFromCahce, namespace, callback);
            return;
        }

        let pendingRequest = this.cmsContentPending.get(language);
        if (!pendingRequest) {
            pendingRequest = CpContentIntegrationApi.get<CmsContentResponse>(
                `/content-integration/${headlessCmsVersion}/${this.getBrand(currentBrand)}/${language}`,
            );
            this.cmsContentPending.set(language, pendingRequest);
        }

        pendingRequest
            .then(res => {
                const data = res.data;
                this.cmsContent.set(language, data);

                this.handleCmsCallback(data, namespace, callback);
            })
            .catch(err => callback(err, null));
    }

    private handleCmsCallback(data: CmsContentResponse, namespace: string, callback: ReadCallback): void {
        const cmsContent = data[namespace];
        const templateParams =
            (typeof cmsContent === 'object' && 'templateParams' in cmsContent ? cmsContent.templateParams : []) || [];

        try {
            const resolvedCmsContent = CmsContentResolver.resolve(cmsContent, templateParams);
            callback(null, resolvedCmsContent);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`[CMS] Failed to resolve content for namespace "${namespace}".`, e);
            throw e;
        }
    }

    private getBrand(brand: string): string {
        switch (brand) {
            case 'audi':
            case 'seat':
            case 'skoda':
            case 'vw':
                return brand;
            case 'vwfs':
                // return 'bluelabel';
                return 'generic';
            default:
                return 'generic';
        }
    }
}

// Workaround to solve an error "You are passing a wrong module! Please check the object you are passing to i18next.use()"
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(CmsBackend as any).type = 'backend';

export default CmsBackend;
